import React, { useState } from 'react';
import { ComplianceTemplate, complianceService, calculateRiskWeight } from '@/firebase/complianceService';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { OWNER_ROLES } from '@/constants/roles';
import { getOwnerRolesForOrganizationType, getDefaultOwnerRoleForOrganizationType } from '@/constants/organizationRoles';
import { X } from 'lucide-react';

interface InlineTemplateFormProps {
  onSave: (template: ComplianceTemplate) => void;
  onCancel: () => void;
  categories: string[];
  suggestedName?: string; // Optional name suggestion from form
}

// Predefined options
const DEFAULT_FREQUENCIES = [
  { value: 'P1Y', label: 'Annually (P1Y)' },
  { value: 'P6M', label: 'Every 6 Months (P6M)' },
  { value: 'P3M', label: 'Every 3 Months (P3M)' },
  { value: 'P1M', label: 'Monthly (P1M)' },
  { value: 'P2Y', label: 'Every 2 Years (P2Y)' },
  { value: 'P5Y', label: 'Every 5 Years (P5Y)' }
];

const InlineTemplateForm: React.FC<InlineTemplateFormProps> = ({ 
  onSave, 
  onCancel, 
  categories,
  suggestedName = '' 
}) => {
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();
  
  // Get organization-type-specific owner roles
  const organizationType = currentOrganization?.industryType || null;
  const availableOwnerRoles = getOwnerRolesForOrganizationType(organizationType);
  const defaultOwnerRole = getDefaultOwnerRoleForOrganizationType(organizationType);
  
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<Partial<ComplianceTemplate>>({
    name: suggestedName,
    category: categories.length > 0 ? categories[0] : '',
    frequency: 'P1Y', // Default to annually
    lead_times_days: [30, 7], // Default alert times
    owner_role: defaultOwnerRole || (availableOwnerRoles.length > 0 ? availableOwnerRoles[0] : ''),
    evidence_types: [],
    pass_fail_criteria: '',
    jurisdiction: '',
    jurisdiction_notes: '',
    risk_level: 'Medium',
    risk_weight: 0.6, // Default for Medium
    mark_due_in_days: 30
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.category) {
      alert('Please enter a template name and select a category');
      return;
    }

    if (!currentOrganization?.id) {
      alert('Organization is required');
      return;
    }

    setIsSaving(true);
    try {
      const userInfo = user ? { 
        id: user.id, 
        name: user.name || 'Unknown', 
        email: user.email || 'Unknown' 
      } : undefined;

      // Calculate risk weight if not set
      const riskWeight = formData.risk_weight ?? calculateRiskWeight(formData.risk_level || 'Medium');

      const templateData: Omit<ComplianceTemplate, 'id'> = {
        name: formData.name,
        category: formData.category,
        frequency: formData.frequency || 'P1Y',
        lead_times_days: formData.lead_times_days || [30, 7],
        owner_role: formData.owner_role || (defaultOwnerRole || availableOwnerRoles[0] || ''),
        evidence_types: formData.evidence_types || [],
        pass_fail_criteria: formData.pass_fail_criteria || '',
        jurisdiction: formData.jurisdiction || '',
        jurisdiction_notes: formData.jurisdiction_notes || '',
        risk_level: formData.risk_level || 'Medium',
        risk_weight: riskWeight,
        mark_due_in_days: formData.mark_due_in_days || 30
      };

      const result = await complianceService.addTemplate(
        templateData,
        currentOrganization.id,
        userInfo
      );

      if (result.success && result.id) {
        // Fetch the created template to get the full template data
        const getResult = await complianceService.getTemplate(result.id);
        if (getResult.success && getResult.data) {
          onSave(getResult.data);
        } else {
          alert(`Template created but could not load details: ${getResult.error || 'Unknown error'}`);
        }
      } else {
        alert(`Error creating template: ${result.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Error creating template:', error);
      alert(`Error creating template: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[130] flex items-center justify-center p-4">
      <div 
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
          <h3 className="text-lg font-semibold text-gray-800">Create New Compliance Template</h3>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700 p-1"
            disabled={isSaving}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800">
              <strong>Quick Setup:</strong> This simplified form creates a basic template. You can edit it later to add more details like evidence types, jurisdiction notes, and reference documents.
            </p>
          </div>

          {/* Name */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Template Name <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Fire Extinguisher Inspection"
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              required
              disabled={isSaving}
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Category <span className="text-red-600">*</span>
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              required
              disabled={isSaving}
            >
              <option value="">Select category...</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Frequency */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Frequency
            </label>
            <select
              value={formData.frequency}
              onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              disabled={isSaving}
            >
              {DEFAULT_FREQUENCIES.map(freq => (
                <option key={freq.value} value={freq.value}>{freq.label}</option>
              ))}
            </select>
          </div>

          {/* Owner Role */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Owner Role
            </label>
            <select
              value={formData.owner_role}
              onChange={(e) => setFormData({ ...formData, owner_role: e.target.value })}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              disabled={isSaving}
            >
              <option value="">Select owner role...</option>
              {availableOwnerRoles.map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>

          {/* Risk Level */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Risk Level
            </label>
            <select
              value={formData.risk_level}
              onChange={(e) => {
                const riskLevel = e.target.value;
                setFormData({ 
                  ...formData, 
                  risk_level: riskLevel,
                  risk_weight: calculateRiskWeight(riskLevel)
                });
              }}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              disabled={isSaving}
            >
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
            </select>
          </div>

          {/* Optional Fields - Collapsible */}
          <details className="border border-gray-200 rounded-md">
            <summary className="px-3 py-2 bg-gray-50 cursor-pointer text-sm font-medium text-gray-700 hover:bg-gray-100">
              Optional Fields (Click to expand)
            </summary>
            <div className="p-3 space-y-3">
              {/* Pass/Fail Criteria */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Pass/Fail Criteria
                </label>
                <textarea
                  value={formData.pass_fail_criteria}
                  onChange={(e) => setFormData({ ...formData, pass_fail_criteria: e.target.value })}
                  placeholder="Describe what constitutes pass or fail..."
                  rows={2}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  disabled={isSaving}
                />
              </div>

              {/* Jurisdiction */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Jurisdiction
                </label>
                <input
                  type="text"
                  value={formData.jurisdiction}
                  onChange={(e) => setFormData({ ...formData, jurisdiction: e.target.value })}
                  placeholder="e.g., County/Fire Marshal"
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  disabled={isSaving}
                />
              </div>

              {/* Jurisdiction Notes */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Jurisdiction Notes
                </label>
                <textarea
                  value={formData.jurisdiction_notes}
                  onChange={(e) => setFormData({ ...formData, jurisdiction_notes: e.target.value })}
                  placeholder="Additional notes about jurisdiction requirements..."
                  rows={2}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  disabled={isSaving}
                />
              </div>
            </div>
          </details>

          {/* Footer */}
          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSaving}
            >
              {isSaving ? 'Creating...' : 'Create Template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InlineTemplateForm;
